import { Chip, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { Icons } from 'utoniq-ui'

import { ItemStatus, SalesCondition } from '@/clients/utoniq-core/schema'
import { ThroughTicketTag } from '@/components/organisms'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type ItemStatusCellProps = {
  salesCondition: string
  isPublished?: boolean
  isDrm: boolean
  isPremium: boolean
  isThroughTicket?: boolean
}

export const ItemStatusCell: Cell<ItemStatusCellProps> = ({
  salesCondition,
  isPublished,
  isDrm,
  isPremium,
  isThroughTicket,
}) => {
  const { t } = useTranslation('consoleItems')

  const salesStatusTag = useMemo(() => {
    switch (salesCondition) {
      case SalesCondition.Active:
        return {
          label: t(ItemStatus.Sale),
          sx: { bgcolor: 'secondary.main' },
          color: 'white',
        }
      case SalesCondition.Before:
        return {
          label: t(ItemStatus.Before),
          sx: { bgcolor: 'grey.100', color: 'text.secondary' },
          color: 'secondary.main',
        }
      case SalesCondition.End:
        return {
          label: t(ItemStatus.End),
          sx: { bgcolor: 'grey.100', color: 'text.secondary' },
          color: 'secondary.main',
        }
      default:
        return {
          label: t(ItemStatus.Pause),
          sx: { bgcolor: 'grey.100', color: 'text.secondary' },
          color: 'secondary.main',
        }
    }
  }, [salesCondition, t])

  const publishingStatusTag = useMemo(() => {
    // 公開中
    if (isPublished) {
      return {
        label: t('publish'),
        sx: { bgcolor: 'primary.main' },
        color: 'white',
      }
    }
    return {
      label: t(ItemStatus.Private),
      sx: { bgcolor: 'grey.100' },
      color: 'text.disabled',
    }
  }, [isPublished, t])

  return (
    <CellFrame sx={{ pb: 2 }}>
      <Stack direction="row" spacing={2}>
        {/* 販売状況を表示するタグ */}
        <Chip
          label={
            <Typography variant="overline" color={salesStatusTag.color}>
              {salesStatusTag.label}
            </Typography>
          }
          sx={{
            ...salesStatusTag.sx,
            p: 1,
            height: 20,
            '.MuiChip-label': { px: 0 },
          }}
        />
        {/* 公開状況を表示するタグ */}
        <Chip
          label={
            <Typography variant="overline" color={publishingStatusTag.color}>
              {publishingStatusTag.label}
            </Typography>
          }
          sx={{
            ...publishingStatusTag.sx,
            p: 1,
            height: 20,
            '.MuiChip-label': { px: 0 },
          }}
        />
        {/* メンバー限定状態を表示するタグ */}
        {isPremium && (
          <Icons.MembersOnly fontSize="small" sx={{ color: 'info.main' }} />
        )}
        {/* DRM状態を表示するタグ */}
        {isDrm && (
          <Chip
            label={
              <Typography variant="overline" color="white">
                {t('drm')}
              </Typography>
            }
            sx={{
              bgcolor: 'primary.dark',
              p: 1,
              height: 20,
              '.MuiChip-label': { px: 0 },
            }}
          />
        )}
        {/* Tag to display ThroughTicket */}
        {isThroughTicket && <ThroughTicketTag />}
      </Stack>
    </CellFrame>
  )
}
